<template>
   <div class="card main-page" style="overflow: hidden;">
      <div class="card-body">

         <!-- Top Bar: Export + Filters -->
         <div class="top-bar">
            <!-- Export Buttons -->
            <div class="export-group">
               <button type="button" class="btn btn-export" @click="copyToClipBoard()">
                  <i class="fa-regular fa-copy me-1"></i> Copy
               </button>
               <button type="button" class="btn btn-export" @click="exportLogData('csv')">
                  <i class="fa-solid fa-file-csv me-1"></i> CSV
               </button>
               <button type="button" class="btn btn-export" @click="exportLogData('xls')">
                  <i class="fa-solid fa-file-excel me-1"></i> Excel
               </button>
               <button type="button" class="btn btn-export" disabled>
                  <i class="fa-solid fa-file-pdf me-1"></i> PDF
               </button>
               <button type="button" class="btn btn-export" disabled @click="showPrintPreview()">
                  <i class="fa-solid fa-print me-1"></i> Print
               </button>
            </div>

            <!-- Quick Filters -->
            <div class="quick-filters">
               <div class="filter-item">
                  <label>Login</label>
                  <select v-model="filterLoginBy" class="form-select form-select-sm">
                     <option v-for="filter in filterLogin" :value="filter.value" :key="filter.value">{{ filter.text }}</option>
                  </select>
               </div>
               <div class="filter-item">
                  <label>OAuth</label>
                  <select v-model="filterOAuthBy" class="form-select form-select-sm">
                     <option v-for="filter in filterOAuth" :value="filter.value" :key="filter.value">{{ filter.text }}</option>
                  </select>
               </div>
               <div class="filter-item">
                  <label>Sort</label>
                  <select v-model="sortBy" class="form-select form-select-sm">
                     <option v-for="sortBy in sortByList" :value="sortBy.value" :key="sortBy.value">{{ sortBy.text }}</option>
                  </select>
               </div>
               <div class="filter-item">
                  <label>Per Page</label>
                  <select v-model="pagination.pageSize" class="form-select form-select-sm">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>
            </div>
         </div>

         <!-- Search & Filter Panel -->
         <div class="filter-panel">
            <div class="row g-2 align-items-end">
               <div class="col-lg-3 col-md-6">
                  <label class="filter-label">Search keyword(s)</label>
                  <input type="text" class="form-control form-control-sm filter-input"
                     v-model="filters['keyword']"
                     @keyup="onSearch()"
                     placeholder="Website, extension name, or key...">
               </div>
               <div class="col-lg-2 col-md-3">
                  <label class="filter-label">Status</label>
                  <select class="form-select form-select-sm filter-input"
                     v-model="filters['is_safe']"
                     @change="onSearch()">
                     <option v-for="(val, key) in statusOptions" :key="key" :value="val">{{ key }}</option>
                  </select>
               </div>
               <div class="col-lg-2 col-md-3">
                  <label class="filter-label">Date From</label>
                  <input type="date" class="form-control form-control-sm filter-input"
                     min="1979-12-31" :max="maxDateFrom"
                     @keydown="preventKeyPress($event)"
                     @change="onSearch()"
                     v-model="filters['date_from']" />
               </div>
               <div class="col-lg-2 col-md-3">
                  <label class="filter-label">Date To</label>
                  <input type="date" class="form-control form-control-sm filter-input"
                     :min="minDateTo" max="2100-12-31"
                     @keydown="preventKeyPress($event)"
                     @change="onSearch()"
                     v-model="filters['date_to']" />
               </div>
               <div class="col-lg-1 col-md-3">
                  <label class="filter-label">Min Size</label>
                  <input type="numeric" class="form-control form-control-sm filter-input"
                     @keypress="isNumber($event)"
                     @keyup="onSearch()"
                     v-model="filters['min_upload_size']"
                     placeholder="Kb">
               </div>
               <div class="col-lg-1 col-md-3">
                  <label class="filter-label">Max Size</label>
                  <input type="numeric" class="form-control form-control-sm filter-input"
                     @keypress="isNumber($event)"
                     @keyup="onSearch()"
                     v-model="filters['max_upload_size']"
                     placeholder="Kb">
               </div>
               <div class="col-lg-1 col-md-3 d-flex align-items-end">
                  <button type="button" class="btn btn-sm btn-clear-filters w-100" @click="ClearFilters()" title="Clear all filters">
                     <i class="fa-solid fa-rotate-left me-1"></i> Reset
                  </button>
               </div>
            </div>
         </div>

         <!-- Table -->
         <div class="table-scroll-container" id="infinite_table">
            <table class="table table-sm align-middle log-table">
               <thead>
                  <tr>
                     <th style="width: 36px;"></th>
                     <template v-for="header in headers" :key="header.value">
                        <th :hidden="(header.text == 'Version' || header.text == 'Browser') && !this.$store.state.auth.isSuperAdmin">
                           {{ header.text }}
                        </th>
                     </template>
                  </tr>
               </thead>

               <tbody v-if="this.logs.length <= 0">
                  <tr>
                     <td colspan="12" class="text-center text-muted py-4">
                        <i class="fa-solid fa-inbox me-2" style="font-size: 18px;"></i>
                        No records to display.
                     </td>
                  </tr>
               </tbody>

               <tbody>
                  <template v-for="(log, index) in logs" :key="index">
                     <tr>
                        <td>
                           <a v-if="hasDetails(log)" @click="openDetailsModal(log)"
                              class="detail-link" :title="getEyeTooltip(log)">
                              <i class="fa-regular fa-eye"></i>
                           </a>
                        </td>

                        <td :title="log.api_key" style="max-width: 120px;">
                           {{ (!log.apikey_name == null || !log.apikey_name == "") ? truncateText(log.apikey_name, 15) :
                              (!log.host_name == null || !log.host_name == "") ? truncateText(log.host_name, 20) :
                                 truncateText(log.api_key, 20)
                           }}
                        </td>
                        <td :title="trimQuestionMark(log.url)" style="max-width: 175px;">
                           {{ trimQuestionMark(log.url) }}
                        </td>
                        <td style="max-width: 175px;" :title="log.redirect_uri">
                           {{ log.redirect_uri }}
                        </td>
                        <td>{{ (log.filesize == null || log.filesize == 0) ? 0 : log.filesize / this.fileSizeDivisor }} Kb</td>

                        <td class="text-center" v-if="log.has_password_field == 1">
                           <span class="badge-yes"><i class="fa-solid fa-check"></i></span>
                        </td>
                        <td class="text-center" v-else>
                           <span class="badge-no"><i class="fa-solid fa-x"></i></span>
                        </td>

                        <td>{{ log.ip }}</td>
                        <td><span :class="getStatusBadgeClass(log.is_safe)">{{ getStatusName(log.is_safe) }}</span></td>
                        <td>
                           <span v-if="log.block_type"
                                 :class="getTypeBadgeClass(log.block_type)"
                                 :title="getBlockTypeTooltip(log)">
                              {{ getBlockTypeDisplay(log) }}
                           </span>
                        </td>
                        <td v-if="this.$store.state.auth.isSuperAdmin">{{ log.extension_version }}</td>
                        <td :title="log.user_agent" v-if="this.$store.state.auth.isSuperAdmin">
                           {{ (log.user_agent == null) ? "Old Version" : log.user_agent.includes("Edg")? "Edge" : log.user_agent.includes("Chrome") ? "Chrome" : log.user_agent.includes("Firefox") ? "Firefox" : "None" }}
                        </td>
                        <td class="date-cell">{{ formatDateFromUTC(log.date_created) }}</td>
                     </tr>
                  </template>
               </tbody>
            </table>
         </div>

         <!-- Footer Status -->
         <div class="table-footer">
            <div class="footer-info">
               <span class="total-badge">
                  <i class="fa-solid fa-list me-1"></i>
                  {{ this.logs.length }} items loaded
               </span>
            </div>
            <div class="footer-status">
               <span v-if="this.logCount >= this.pagination.pageSize" class="scroll-hint">
                  <i class="fa-solid fa-angles-down me-1"></i>
                  Scroll to load more
               </span>
               <span v-else-if="this.logs.length != 0" class="end-hint">
                  <i class="fa-solid fa-circle-check me-1"></i>
                  All records loaded
               </span>
            </div>
         </div>

      </div>
   </div>

   <div v-if="showTable" class="print-table"></div>

   <LoadingScreen v-if="isLoading" />

   <!-- Details Modal -->
   <div class="modal-backdrop-custom" v-if="showDetailsModal" @click.self="closeDetailsModal()">
      <div class="modal-content-custom">
         <button @click="closeDetailsModal()" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-circle-info me-2" style="color: #00a0c1;"></i>
            Log Details
         </h5>
         <hr>

         <div class="modal-body-custom" v-if="selectedLog">

            <!-- Block Reason Badge -->
            <div class="detail-section" v-if="selectedLog.unsafe_code && selectedLog.unsafe_code !== '0'">
               <div class="detail-label">Block Reason & Code</div>
               <div class="d-flex align-items-center gap-2 flex-wrap">
                  <div class="detail-badge detail-badge--reason">
                     <i class="fa-solid fa-shield-halved me-1"></i>
                     {{ getBlockReasonLabel(selectedLog.unsafe_code) }}
                  </div>
                  <span class="badge bg-secondary" style="font-size: 11px; font-weight: 600;">Code: {{ selectedLog.unsafe_code }}</span>
               </div>
               <div v-if="getBlockReasonDescription(selectedLog.unsafe_code)" class="detail-description-text mt-2 text-muted">
                  {{ getBlockReasonDescription(selectedLog.unsafe_code) }}
               </div>
            </div>

            <!-- Block Type -->
            <div class="detail-section" v-if="selectedLog.block_type">
               <div class="detail-label">Block Type</div>
               <span :class="getTypeBadgeClass(selectedLog.block_type)" style="font-size: 12px;">
                  {{ getBlockTypeDisplay(selectedLog) }}
               </span>
            </div>

            <!-- Phishing Protection (code 316) -->
            <div class="detail-section" v-if="selectedLog.unsafe_code === '316'">
               <div class="detail-label">
                  <i class="fa-solid fa-fish-fins me-1" style="color: #ef4444;"></i>
                  Phishing Protection
               </div>
               <div class="detail-value detail-value--highlight detail-value--phishing">
                  <i class="fa-solid fa-key me-1"></i>
                  Password Field Detected on Unapproved Website
               </div>
               <small class="text-muted d-block mt-1">Phishing protection prevents credential harvesting by blocking login forms and password inputs on unapproved domains.</small>
            </div>

            <!-- Government TLD (code 361) -->
            <div class="detail-section" v-if="selectedLog.unsafe_code === '361'">
               <div class="detail-label">
                  <i class="fa-solid fa-landmark me-1" style="color: #6366f1;"></i>
                  Government TLD
               </div>
               <div class="detail-value detail-value--highlight detail-value--tld">
                  {{ extractDomainFromInfo(selectedLog.other_information || selectedLog.other_other_information) || trimQuestionMark(selectedLog.url) }}
               </div>
               <small class="text-muted d-block mt-1">This domain was blocked because it matches a restricted government TLD.</small>
            </div>

            <!-- AI Category (code 362) -->
            <div class="detail-section" v-if="selectedLog.unsafe_code === '362'">
               <div class="detail-label">
                  <i class="fa-solid fa-robot me-1" style="color: #8b5cf6;"></i>
                  AI Website Blocked
               </div>
               <div class="detail-value detail-value--highlight detail-value--ai">
                  {{ extractDomainFromInfo(selectedLog.other_information || selectedLog.other_other_information) || trimQuestionMark(selectedLog.url) }}
               </div>
               <small class="text-muted d-block mt-1">This domain was blocked by the AI Category policy.</small>
            </div>

            <!-- Category block (code 355) -->
            <div class="detail-section" v-if="selectedLog.unsafe_code === '355'">
               <div class="detail-label">
                  <i class="fa-solid fa-tags me-1" style="color: #f59e0b;"></i>
                  Blocked Category
               </div>
               <div class="detail-value detail-value--highlight detail-value--category">
                  {{ selectedLog.category || 'Unknown Category' }}
               </div>
               <div v-if="selectedLog.sub_category" class="mt-2">
                  <div class="detail-label">Sub-Category</div>
                  <div class="detail-value">{{ selectedLog.sub_category }}</div>
               </div>
               <small class="text-muted d-block mt-1">This domain was blocked because it falls under a restricted category.</small>
            </div>

            <!-- Category & Sub-Category (other codes with category data) -->
            <div class="detail-section" v-else-if="selectedLog.category || selectedLog.sub_category">
               <div v-if="selectedLog.category">
                  <div class="detail-label">Category</div>
                  <div class="detail-value">{{ selectedLog.category }}</div>
               </div>
               <div v-if="selectedLog.sub_category" class="mt-2">
                  <div class="detail-label">Sub-Category</div>
                  <div class="detail-value">{{ selectedLog.sub_category }}</div>
               </div>
            </div>

            <!-- Other Information (generic for non-TLD/AI/Category codes) -->
            <div class="detail-section" v-if="(selectedLog.other_information || selectedLog.other_other_information) && selectedLog.unsafe_code !== '361' && selectedLog.unsafe_code !== '362'">
               <div class="detail-label">Additional Details</div>
               <p class="detail-text">{{ selectedLog.other_information || selectedLog.other_other_information }}</p>
            </div>

            <!-- URL -->
            <div class="detail-section">
               <div class="detail-label">URL</div>
               <p class="detail-text" style="word-break: break-all;">{{ trimQuestionMark(selectedLog.url) }}</p>
            </div>

            <!-- Fallback: No details -->
            <div v-if="!selectedLog.other_information && !selectedLog.other_other_information && !selectedLog.category && !selectedLog.sub_category && (!selectedLog.unsafe_code || selectedLog.unsafe_code === '0')">
               <p class="text-muted">No additional details available for this log.</p>
            </div>
         </div>

         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm" style="background: #e9ecef; border-radius: 8px;" @click="closeDetailsModal()">
               Close
            </button>
         </div>
      </div>
   </div>
</template>

<script>
import moment from 'moment'
import { debounce, extractErrorMessage } from '@/plugins/helper.js'
import exportFromJSON from 'export-from-json'
import LoadingScreen from '@/components/Analytics/Log History/Loading.vue'

export default ({

   components: {
      LoadingScreen
   },
   el: '#infinite_table',
   data() {
      return {
         showTable: false,
         showDetailsModal: false,
         selectedLog: null,
         headers: [
            { text: "Extension Name/Key", value: "nameOrExtension" },
            { text: "Website", value: "website" },
            { text: "Redirect URL", value: "redirectURL" },
            { text: "Size (Kb)", value: "size_kb" },
            { text: "Login Field", value: "login_field" },
            { text: "IP", value: "ip" },
            { text: "Status", value: "status" },
            { text: "Block Type", value: "type" },
            { text: "Version", value: "extension_version" },
            { text: "Browser", value: "user_agent" },
            { text: "Date & Time", value: "dateCreated" },
         ],
         json_data: [
            {
               'name': 'Tony Peña',
               'city': 'New York',
               'country': 'United States',
               'birthdate': '1978-03-15',
               'phone': {
                  'mobile': '1-541-754-3010',
                  'landline': '(541) 754-3010'
               }
            },
            {
               'name': 'Thessaloniki',
               'city': 'Athens',
               'country': 'Greece',
               'birthdate': '1987-11-23',
               'phone': {
                  'mobile': '+1 855 275 5071',
                  'landline': '(2741) 2621-244'
               }
            }
         ],
         fileSizeDivisor: 1000,
         isLoading: false,
         search: "",
         filterActive: true,
         filters: {
            "keyword": null,
            "url": null,
            "email": null,
            "ext_api_key": null,
            "is_safe": -1,
            "date_from": moment().subtract(1, 'days').format("YYYY-MM-DD"),
            "date_to": moment().format("YYYY-MM-DD"),
            "min_upload_size": null,
            "max_upload_size": null,
            "has_password_field": false,
            "oauth": false,
         },
         extensionKeys: [],
         statusOptions: {
            "All": -1, "Allowed": 1, "Blocked": 3, "Unchecked": 2
         },
         filterOAuth: [
            { text: "With OAuth", value: 1 },
            { text: "No OAuth", value: 0 },
            { text: "Both", value: 2 }
         ],
         filterOAuthBy: 2,
         filterLogin: [
            { text: "With Login", value: 1 },
            { text: "No Login", value: 0 },
            { text: "Both", value: 2 }
         ],
         filterLoginBy: 2,
         debounceFn: null,
         logs: [],
         logsExport: [],
         logsExportAll: [],
         paginationData: {},
         logCount: 0,
         isFromRowsPerPage: false,
         pagination: {
            pageSize: 50,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "Date: Newest", value: "dateCreated_desc" },
            { text: "Date: Oldest", value: "dateCreated_asc" },
         ],
         sortBy: "dateCreated_desc",
          sorting: {
            field: "name",
            order: "desc"
         },
         useDummyData: false,
      }
   },

   computed: {
      currentPage() {
         return this.pagination.page
      },

      rowsPerPage() {
         return this.pagination.pageSize
      },

      sortItems() {
         return this.sortBy
      },

      filterByOAuth() {
         return this.filterOAuthBy
      },

      filterByLogin() {
         return this.filterLoginBy
      },

      todayDate() {
         return moment().format("YYYY-MM-DD");
      },

      maxDateFrom() {
         if (this.filters['date_to']) {
            return this.filters['date_to'];
         }
         return '2100-12-31';
      },

      minDateTo() {
         return this.filters['date_from'] || '1979-12-31';
      },
   },

   watch: {
      currentPage() {
      },

      filterByOAuth() {
         this.pagination.page = 1
         this.logs = []
         this.getLogHistory()
      },

      filterByLogin() {
         this.pagination.page = 1
         this.logs = []
         this.getLogHistory()
      },

      generateDownloadHeaders() {

      },

      rowsPerPage() {
         this.pagination.page = 1
         this.isFromRowsPerPage = true
         this.logs = []
         this.scrollTop()
         this.onScrollEnd()
         this.getLogHistory()
      },

      sortItems() {
         this.pagination.page = 1
         this.logs = []
         if (this.sortBy.includes("_")) {
            let sort = this.sortBy.split("_")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getLogHistory()
      },

      'filters.date_from'(newVal) {
         if (newVal) {
            if (!this.filters.date_to || newVal > this.filters.date_to) {
               this.filters.date_to = newVal
            }
         }
      },

      'filters.date_to'(newVal) {
         if (newVal) {
            if (this.filters.date_from && newVal < this.filters.date_from) {
               this.filters.date_from = newVal
            }
         }
      },
   },

   created() {
      this.init()
      this.debounceFn = debounce(() => this.getLogHistory(), 500)
   },

   mounted() {
      this.onScrollEnd()
   },

   methods: {

      openDetailsModal(log) {
         this.selectedLog = log;
         this.showDetailsModal = true;
      },

      closeDetailsModal() {
         this.showDetailsModal = false;
         this.selectedLog = null;
      },

      hasDetails(log) {
         return !!(log.other_information || log.category || log.sub_category || log.other_other_information
            || (log.unsafe_code && log.unsafe_code !== '0' && log.unsafe_code !== ''));
      },

      getBlockTypeDisplay(log) {
         if (!log || !log.block_type) return ''
         if (log.unsafe_code && log.unsafe_code !== '0' && log.unsafe_code !== '') {
            return `${log.block_type} (${log.unsafe_code})`
         }
         return log.block_type
      },

      getBlockTypeTooltip(log) {
         if (!log) return ''
         if (log.unsafe_code && log.unsafe_code !== '0' && log.unsafe_code !== '') {
            const reason = this.getBlockReasonLabel(log.unsafe_code)
            return reason ? `${reason} (Code ${log.unsafe_code})` : `Code: ${log.unsafe_code}`
         }
         return log.block_type || ''
      },

      getEyeTooltip(log) {
         if (log && log.unsafe_code && log.unsafe_code !== '0' && log.unsafe_code !== '') {
            return `View Details (Code ${log.unsafe_code})`
         }
         return 'View Details'
      },

      getBlockReasonLabel(code) {
         const labels = {
            '315': 'Blocked by Admin (Regular)',
            '316': 'Phishing Protection',
            '346': 'HTTP Website Blocked',
            '347': 'New Website (< 4 months)',
            '348': 'Google Safe Browsing',
            '349': 'Webshrinker — Unsafe Domain',
            '350': 'Unsafe Domain Status',
            '351': 'File Sharing Blocked',
            '352': 'Email in URL',
            '353': 'Domain Too Long (> 40 chars)',
            '354': 'Chat/Messaging Blocked',
            '355': 'Category Blocked',
            '356': 'In Unsafe Websites List',
            '357': 'Block All (Not Whitelisted)',
            '358': 'Suspicious .info Domain',
            '359': 'Unsafe (6+ months)',
            '360': 'OAuth Website Blocked',
            '361': 'Government TLD Blocked',
            '362': 'AI Category Blocked',
         }
         return labels[String(code)] || `Code ${code}`
      },

      getBlockReasonDescription(code) {
         const descriptions = {
            '315': 'This website is explicitly blocklisted by your organisation\'s administrative security policy.',
            '316': 'Phishing protection is enabled. A password input field was detected on an unapproved or untrusted website, blocking potential credential theft.',
            '346': 'This site does not support secure HTTPS encryption. Unencrypted connections are blocked to protect your data.',
            '347': 'This domain was registered very recently (less than 4 months ago). New domains are frequently used for temporary phishing or malicious campaigns.',
            '348': 'This site has been flagged as a malicious, deceptive, or harmful website by Google Safe Browsing.',
            '349': 'This domain has been classified under an unsafe or high-risk category by security intelligence databases.',
            '350': 'The domain safety status is flagged as unsafe or high-risk.',
            '351': 'Access to file-sharing and cloud-storage sites is restricted to prevent unauthorized data transfers.',
            '352': 'The requested URL contains embedded email addresses or formatting commonly used in phishing scams.',
            '353': 'The domain name is unusually long, which is a common indicator of defensive registration or phishing spoofing.',
            '354': 'Chat and instant messaging platforms are restricted to enforce productivity and secure communications.',
            '355': 'This website falls under a restricted content category blocked by your organisation\'s content filtering policy.',
            '356': 'This domain is explicitly listed in your organisation\'s unsafe/restricted websites blocklist.',
            '357': 'Your organisation has a strict zero-trust policy enabled. Only explicitly allowlisted websites are accessible.',
            '358': 'This is a .info domain with hyphens, which is a combination highly correlated with phishing and fraudulent sites.',
            '359': 'This website has been flagged and tracked as unsafe or high-risk for over 6 months.',
            '360': 'OAuth login endpoints are restricted on this domain to protect corporate credentials.',
            '361': 'Restricted top-level government domain blocked under your organisation\'s security policy.',
            '362': 'Artificial Intelligence and LLM services are restricted under your organisation\'s policy.',
         }
         return descriptions[String(code)] || ''
      },

      extractDomainFromInfo(info) {
         if (!info) return null
         // Backend stores: "Domain blocked due to government tld settings: example.gov.au"
         // or: "Domain blocked due to AI category settings: chatgpt.com"
         const colonIdx = info.lastIndexOf(': ')
         if (colonIdx > -1) {
            return info.substring(colonIdx + 2).trim()
         }
         return info
      },

      // Status badge classes (pill-style)
      getStatusBadgeClass(status) {
         switch (status) {
            case 0:
            case 3:
               return 'status-badge status-blocked'
            case 1:
               return 'status-badge status-safe'
            case 2:
            default:
               return 'status-badge status-unchecked'
         }
      },

      getTypeBadgeClass(type) {
         if (typeof type === 'string') {
            type = type.replace(/\s*\(\d+\)$/, '').toLowerCase().trim();
         }
         switch (type) {
            case "phishing":
               return 'type-badge type-phishing';
            case "categories":
            case "category":
               return 'type-badge type-categories';
            case "ai categories":
            case "ai_categories":
               return 'type-badge type-ai-categories';
            case "government tlds":
            case "government_tlds":
               return 'type-badge type-government-tlds';
            case "download":
               return 'type-badge type-download';
            case "upload":
               return 'type-badge type-upload';
            case "block all websites":
            case "block_all_websites":
            case "block_all":
               return 'type-badge type-block-all-websites';
            case "regular":
               return 'type-badge type-regular';
            case "regular - ip":
            case "regular-ip":
               return 'type-badge type-regular-ip';
            case "http block":
            case "http_block":
            case "block http enabled":
               return 'type-badge type-http-block';
            case "unsafe":
               return 'type-badge type-unsafe';
            default:
               return 'type-badge type-default';
         }
      },

      init() {
         this.getLogHistory()
         this.getExtensionKeys()
      },

      scrollTop() {
         let div = document.querySelector("#infinite_table");
         div.removeEventListener("scroll", this.trialListener)
      },

      onScrollEnd() {
         let div = document.querySelector("#infinite_table");
         div.addEventListener(
            "scroll",
            this.trialListener,
            {
               passive: false
            }
         );

         div.divparam = div

      },

      trialListener(evt) {
         let div = evt.currentTarget.divparam
         if (Math.abs(div.scrollHeight - div.clientHeight - div.scrollTop) < 1 && this.isFromRowsPerPage == false) {
            if (this.pagination.pageSize <= this.logCount) {
               this.pagination.page += 1
               this.getLogHistory();
            }
         }

         this.isFromRowsPerPage = false;
      },

      async copyToClipBoard() {

         let data = []

         let logLen = 0

         if (this.logs != null) {
            logLen = this.logs.length

            for (let x = 0; x < logLen; x++) {
               data[x] = this.logs[x]
            }
         }


         if (data != null) {
            if (data.length > 0) {
               for (let logLen = 0; logLen < data.length; logLen++) {
                  delete data[logLen].account_id
                  delete data[logLen].apikey_id
                  delete data[logLen].id
                  data[logLen].date_created = this.formatDate(data[logLen].date_created)
               }
            }
         }

         var jsonString = JSON.stringify(data);

         navigator.clipboard.writeText(jsonString).then(function () {

         }).catch(function (err) {
            console.error('Unable to copy object to clipboard.', err);
            return
         });

         this.$toast.success("Logs copied to clipboard.")
      },

      async exportLogData(str) {

         this.isLoading = true;

         await this.getLogsForExport().then(() => {

            setTimeout(() => {
               const fileName = this.$store.state.auth.organisation_name + '_log_history_' + moment()

               let data = []
               let exportType = null

               switch (str) {
                  case "csv": exportType = exportFromJSON.types.csv
                     break;
                  case "xls": exportType = exportFromJSON.types.xls
                     break;
               }

               let logLen = 0

               if (this.logsExport != null) {
                  logLen = this.logsExport.length

                  if (this.$store.state.auth.isSuperAdmin) {
                     for (let x = 0; x < logLen; x++) {
                        data[x] = {
                           "Extension Key": this.logsExport[x].api_key,
                           "Extension Name": (this.logsExport[x].apikey_name == "" || this.logsExport[x].apikey_name == null) ? "None" : this.logsExport[x].apikey_name,
                           "Host Name": (this.logsExport[x].host_name == null || this.logsExport[x].host_name == "") ? "None" : this.logsExport[x].host_name,
                           "Website": this.logsExport[x].url.replaceAll(";", "?"),
                           "Redirect URL": (this.logsExport[x].redirect_uri == "" || this.logsExport[x].redirect_uri == null) ? "None" : encodeURIComponent(this.logsExport[x].redirect_uri),
                           "Size (Kb)": (this.logsExport[x].filesize == null) ? 0 : this.logsExport[x].filesize,
                           "Login Field": (this.logsExport[x].has_password_field) ? "Yes" : "No",
                           "IP": this.logsExport[x].ip,
                           "Status": this.getStatusName(this.logsExport[x].is_safe),
                           "Block Type": this.getBlockTypeDisplay(this.logsExport[x]),
                           "Category": this.logsExport[x].category,
                           "Sub-Category": this.logsExport[x].sub_category,
                           "Other Details": this.logsExport[x].other_information,
                           "Version": this.logsExport[x].extension_version,
                           "Browser": (this.logsExport[x].user_agent !== "" && this.logsExport[x].user_agent !== null) ? (this.logsExport[x].user_agent.includes("Edg")) ? "Edge" : (this.logsExport[x].user_agent.includes("Chrome"))  ? "Chrome" : (this.logsExport[x].user_agent.includes("Firefox")) ? "Firefox" : "None" : "None",
                           "Date & Time": this.formatDate(this.logsExport[x].date_created)
                        }
                     }
                  }
                  else {
                     for (let x = 0; x < logLen; x++) {
                        data[x] = {
                           "Extension Key": this.logsExport[x].api_key,
                           "Extension Name": (this.logsExport[x].apikey_name == "" || this.logsExport[x].apikey_name == null) ? "None" : this.logsExport[x].apikey_name,
                           "Host Name": (this.logsExport[x].host_name == null || this.logsExport[x].host_name == "") ? "None" : this.logsExport[x].host_name,
                           "Website": this.logsExport[x].url.replaceAll(";", "?"),
                           "Redirect URL": (this.logsExport[x].redirect_uri == "" || this.logsExport[x].redirect_uri == null) ? "None" : this.logsExport[x].redirect_uri,
                           "Size (Kb)": (this.logsExport[x].filesize == null) ? 0 : this.logsExport[x].filesize,
                           "Login Field": (this.logsExport[x].has_password_field) ? "Yes" : "No",
                           "IP": this.logsExport[x].ip,
                           "Status": this.getStatusName(this.logsExport[x].is_safe),
                           "Block Type": this.getBlockTypeDisplay(this.logsExport[x]),
                           "Category": this.logsExport[x].category,
                           "Sub-Category": this.logsExport[x].sub_category,
                           "Other Details": this.logsExport[x].other_information,
                           "Date & Time": this.formatDate(this.logsExport[x].date_created)
                        }
                     }
                  }
               }

               try {
                  exportFromJSON({ data, fileName, exportType })
               } catch (err) {
                  console.error(err)
                  this.$toast.error(extractErrorMessage(err, "Failed to export log data."))
               }

               this.isLoading = false


            }, 5000);


         })

      },

      showPrintPreview() {

         var css = '@page { size: landscape; }',
            head = document.head || document.getElementsByTagName('head')[0],
            style = document.createElement('style');

         style.type = 'text/css';
         style.media = 'print';

         if (style.styleSheet) {
            style.styleSheet.cssText = css;
         } else {
            style.appendChild(document.createTextNode(css));
         }

         head.appendChild(style);


         this.showTable = true;
         window.print();
         this.showTable = false;
      },

      isNumber: function (evt) {
         evt = (evt) ? evt : window.event;
         var charCode = (evt.which) ? evt.which : evt.keyCode;
         if ((charCode > 31 && (charCode < 48 || charCode > 57)) || charCode === 46) {
            evt.preventDefault();
         } else {
            return true;
         }
      },

      preventKeyPress: function (evt) {
         evt = (evt) ? evt : window.event;
         evt.preventDefault();
      },

      getStatusName(status) {
         switch (status) {
            case 0:
            case 3:
               return `BLOCKED`
            case 1:
               return `ALLOWED`
            case 2:
               return `UNCHECKED`
            default:
               return `UNCHECKED`
         }
      },

      getStatusClass(status) {
         switch (status) {
            case 0:
            case 3:
               return `text-color-blocked`
            case 1:
               return `text-color-safe`
            case 2:
            default:
               return `text-color-unchecked`
         }
      },

      getTypeClass(type) {

         if (typeof text === 'string') {
            type = type?.toLowerCase()
         }

         switch (type) {
            case "phishing":
               return `text-color-phishing`
            case "categories":
               return `text-color-blocked`
            default:
               return `text-color-regular`
         }
      },

      StatusCheck() {
      },

      FilterOnOff() {
         this.filterActive = !this.filterActive

         if (this.filterActive) {
            this.$toast.success("Filters Turned On")
         } else {
            this.$toast.error("Filters Turned Off")
         }

         this.getLogHistory()
      },

      changeFilter(field_name) {
         this.filters[field_name] = !this.filters[field_name]
         this.getLogHistory()
      },

      ClearFilters() {
         this.pagination.page = 1
         this.logs = []
         this.filters["keyword"] = null
         this.filters["url"] = null
         this.filters["is_safe"] = -1
         this.filters["email"] = null
         this.filters["ext_api_key"] = null
         this.filters["date_from"] = null
         this.filters["date_to"] = null
         this.filters["min_upload_size"] = null
         this.filters["max_upload_size"] = null
         this.filters["has_password_field"] = false
         this.filters["oauth"] = false
         this.getLogHistory()
      },

      toUTC(date, isTo) {
         if (!date || date === "" || date === "Invalid date") return ""
         let dNow = moment(date)
         if (!dNow.isValid()) return ""

         if (isTo) {
            dNow = dNow.endOf('day')
         } else {
            dNow = dNow.startOf('day')
         }

         return moment.utc(dNow).format()
      },

      formatDate(date) {
         if (!date) return "-"
         let m = moment.utc(date).local()
         return m.isValid() ? m.format("DD-MMM-YYYY @ HH:mm:ss") : "-"
      },

      formatDateFromUTC(date) {
         if (!date) return "-"
         let m = moment.utc(date).local()
         return m.isValid() ? m.format("DD-MMM-YYYY @ HH:mm:ss") : "-"
      },

      formatDateFrom(date) {
         return moment(date).format("YYYY/MM/DD")
      },

      formatDateTo(date) {
         return moment(date).format("YYYY/MM/DD")
      },

      nextPage() {
         let isTrue = this.logCount >= this.pagination.pageSize
         isTrue ? this.pagination.page += 1 : ''
         isTrue ? this.getLogHistory() : null

      },

      onSearch() {

         if (this.filterActive == false)
            return

         this.pagination.page = 1
         this.logs = []

         this.debounceFn()
      },

      async getExtensionKeys() {

         await this.$axios.get("analytics/" + this.$store.state.auth.organisation_uuid + "/get-account-extension-keys")
            .then((response) => {
               this.extensionKeys = response.data.api_keys
            })
            .catch((error) => {
               this.$toast.error(extractErrorMessage(error, "Failed to load extension keys."))
            })
      },

      truncateText(text, limit) {

         if (text === null)
            return
         if (text === '')
            return

         if (text.length <= limit) {
            return text;
         } else {
            return text.slice(0, limit) + '...';
         }
      },

      trimQuestionMark(text) {
         let index = text.indexOf('?')

         if (index > 0) {
            return text.substring(0, index)
         } else {
            return text
         }
      },

      async getLogsForExport() {
         let apiPath = "analytics/" + this.$store.state.auth.organisation_uuid + "/search-account-log-history?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page + "&oauth=" + this.filterOAuthBy + "&has_password_field=" + this.filterLoginBy

         if (this.sorting.order != "") {
            apiPath += "&order_by=" + this.sorting.order
         }

         if (this.filterActive == true) {
            for (const [key, value] of Object.entries(this.filters)) {
               if (key == 'is_safe') {
                  if (value >= 0) {
                     apiPath += `&${key}=${value}`
                  }
               } else if (key == 'date_from') {
                  if (value && value !== "" && value !== "Invalid date") {
                     const utcDate = this.toUTC(value, false)
                     if (utcDate && utcDate !== "Invalid date") {
                        apiPath += `&${key}=${utcDate}`
                     }
                  }
               } else if (key == 'date_to') {
                  if (value && value !== "" && value !== "Invalid date") {
                     const utcDate = this.toUTC(value, true)
                     if (utcDate && utcDate !== "Invalid date") {
                        apiPath += `&${key}=${utcDate}`
                     }
                  } else if (this.filters['date_from'] && this.filters['date_from'] !== "" && this.filters['date_from'] !== "Invalid date") {
                     // Bound to end of date_from's day if date_to is not specified
                     const utcDate = this.toUTC(this.filters['date_from'], true)
                     if (utcDate && utcDate !== "Invalid date") {
                        apiPath += `&${key}=${utcDate}`
                     }
                  }
               } else {
                  if (value !== null && value !== "") {
                     apiPath += `&${key}=${value}`
                  }
               }
            }

            apiPath += `&for_export=1`
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.logs === "No Record Found" || !response.data.logs) {
                  this.logsExport = []
               }
               else {
                  this.logsExport = response.data.logs
               }
            })
            .catch((error) => {
               this.$toast.error(extractErrorMessage(error, "Failed to export logs."))
            })

      },

      async getLogHistory() {

         if (this.useDummyData) {
            const dummyLogs = [
               { api_key: 'ext_a1b2c3d4', apikey_name: 'Office PC #1', host_name: 'DESKTOP-A1B2', url: 'https://mail.google.com/mail/inbox', redirect_uri: '', filesize: 0, has_password_field: 1, ip: '192.168.1.10', is_safe: 1, unsafe_code: '', block_type: '', extension_version: '3.1.2', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T08:15:30Z', category: 'Business', sub_category: 'Email', other_information: '' },
               { api_key: 'ext_e5f6g7h8', apikey_name: 'Reception Desk', host_name: 'FRONT-DESK', url: 'https://fakebank-login.phishing.xyz/signin', redirect_uri: 'https://evil-collector.net/steal', filesize: 0, has_password_field: 1, ip: '192.168.1.22', is_safe: 0, unsafe_code: '348', block_type: 'phishing', extension_version: '3.1.2', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T08:12:05Z', category: 'Deceptive', sub_category: 'Phishing', other_information: 'Known phishing domain targeting banking credentials.' },
               { api_key: 'ext_i9j0k1l2', apikey_name: 'HR Laptop', host_name: 'HR-LAPTOP-01', url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', redirect_uri: '', filesize: 0, has_password_field: 0, ip: '192.168.1.35', is_safe: 3, unsafe_code: '355', block_type: 'categories', extension_version: '3.1.1', user_agent: 'Mozilla/5.0 Firefox/119.0', date_created: '2026-04-23T07:58:42Z', category: 'Streaming Media', sub_category: 'Video Streaming', other_information: '' },
               { api_key: 'ext_m3n4o5p6', apikey_name: 'Dev Machine', host_name: 'DEV-WS-04', url: 'https://github.com/xsurflog/extension', redirect_uri: '', filesize: 45200, has_password_field: 0, ip: '192.168.1.50', is_safe: 1, unsafe_code: '', block_type: '', extension_version: '3.1.2', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T07:45:18Z', category: 'Information Technology', sub_category: 'Source Code', other_information: '' },
               { api_key: 'ext_a1b2c3d4', apikey_name: 'Office PC #1', host_name: 'DESKTOP-A1B2', url: 'https://drive.google.com/file/d/1abc/view', redirect_uri: '', filesize: 152000, has_password_field: 0, ip: '192.168.1.10', is_safe: 1, unsafe_code: '', block_type: '', extension_version: '3.1.2', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T07:30:00Z', category: 'Media Sharing', sub_category: 'Cloud Storage', other_information: 'File upload detected: report_Q1.pdf' },
               { api_key: 'ext_q7r8s9t0', apikey_name: 'Marketing PC', host_name: 'MKT-PC-02', url: 'https://www.reddit.com/r/technology', redirect_uri: '', filesize: 0, has_password_field: 0, ip: '192.168.1.42', is_safe: 3, unsafe_code: '355', block_type: 'categories', extension_version: '3.1.0', user_agent: 'Edg/120.0.2210.91', date_created: '2026-04-23T07:22:15Z', category: 'Social Networking', sub_category: 'Forums', other_information: '' },
               { api_key: 'ext_u1v2w3x4', apikey_name: '', host_name: 'UNKNOWN-PC', url: 'http://crack-software-free.xyz/keygen', redirect_uri: '', filesize: 0, has_password_field: 0, ip: '192.168.1.88', is_safe: 0, unsafe_code: '349', block_type: 'Regular', extension_version: '3.0.9', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T06:55:33Z', category: 'Hacking & Cracking', sub_category: 'Warez', other_information: 'Malware distribution site detected.' },
               { api_key: 'ext_e5f6g7h8', apikey_name: 'Reception Desk', host_name: 'FRONT-DESK', url: 'https://outlook.office365.com/mail', redirect_uri: '', filesize: 0, has_password_field: 1, ip: '192.168.1.22', is_safe: 1, unsafe_code: '', block_type: '', extension_version: '3.1.2', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T06:40:10Z', category: 'Business', sub_category: 'Email', other_information: '' },
               { api_key: 'ext_y5z6a7b8', apikey_name: 'Accounts Dept', host_name: 'ACC-PC-01', url: 'https://www.xero.com/au/login', redirect_uri: 'https://login.xero.com/identity', filesize: 0, has_password_field: 1, ip: '192.168.1.60', is_safe: 2, unsafe_code: '', block_type: '', extension_version: '3.1.2', user_agent: 'Mozilla/5.0 Firefox/119.0', date_created: '2026-04-23T06:30:22Z', category: 'Economy & Finance', sub_category: 'Accounting', other_information: '' },
               { api_key: 'ext_m3n4o5p6', apikey_name: 'Dev Machine', host_name: 'DEV-WS-04', url: 'https://stackoverflow.com/questions/12345', redirect_uri: '', filesize: 0, has_password_field: 0, ip: '192.168.1.50', is_safe: 1, unsafe_code: '', block_type: '', extension_version: '3.1.2', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T06:15:45Z', category: 'Information Technology', sub_category: 'Q&A', other_information: '' },
               { api_key: 'ext_i9j0k1l2', apikey_name: 'HR Laptop', host_name: 'HR-LAPTOP-01', url: 'https://www.seek.com.au/jobs', redirect_uri: '', filesize: 0, has_password_field: 0, ip: '192.168.1.35', is_safe: 1, unsafe_code: '', block_type: '', extension_version: '3.1.1', user_agent: 'Mozilla/5.0 Firefox/119.0', date_created: '2026-04-23T06:00:12Z', category: 'Jobs & Careers', sub_category: 'Job Search', other_information: '' },
               { api_key: 'ext_q7r8s9t0', apikey_name: 'Marketing PC', host_name: 'MKT-PC-02', url: 'https://wetransfer.com/downloads/abc123', redirect_uri: '', filesize: 89500, has_password_field: 0, ip: '192.168.1.42', is_safe: 3, unsafe_code: '351', block_type: 'Upload', extension_version: '3.1.0', user_agent: 'Edg/120.0.2210.91', date_created: '2026-04-23T05:48:30Z', category: 'Media Sharing', sub_category: 'File Transfer', other_information: 'DLP: Upload blocked. File: campaign_assets.zip (89.5 KB)' },
               { api_key: 'ext_a1b2c3d4', apikey_name: 'Office PC #1', host_name: 'DESKTOP-A1B2', url: 'https://www.google.com/search?q=weather', redirect_uri: '', filesize: 0, has_password_field: 0, ip: '192.168.1.10', is_safe: 1, unsafe_code: '', block_type: '', extension_version: '3.1.2', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T05:30:00Z', category: 'Search Engines & Portals', sub_category: '', other_information: '' },
               { api_key: 'ext_c9d0e1f2', apikey_name: 'Guest WiFi PC', host_name: '', url: 'http://gambling-online.bet/poker', redirect_uri: '', filesize: 0, has_password_field: 0, ip: '10.0.0.15', is_safe: 3, unsafe_code: '355', block_type: 'categories', extension_version: '3.1.2', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T05:15:20Z', category: 'Gambling', sub_category: 'Online Gambling', other_information: '' },
               { api_key: 'ext_u1v2w3x4', apikey_name: '', host_name: 'UNKNOWN-PC', url: 'https://login.microsoftonline.com', redirect_uri: 'https://portal.azure.com', filesize: 0, has_password_field: 1, ip: '192.168.1.88', is_safe: 1, unsafe_code: '', block_type: '', extension_version: '3.0.9', user_agent: 'Chrome/120.0.6099.130', date_created: '2026-04-23T05:00:05Z', category: 'Business', sub_category: 'Cloud Services', other_information: '' },
            ]
            this.logs.push(...dummyLogs)
            this.logCount = dummyLogs.length
            return
         }

         let apiPath = "analytics/" + this.$store.state.auth.organisation_uuid + "/search-account-log-history?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page + "&oauth=" + this.filterOAuthBy + "&has_password_field=" + this.filterLoginBy

         if (this.sorting.order != "") {
            apiPath += "&order_by=" + this.sorting.order
         }

         if (this.filterActive == true) {
            for (const [key, value] of Object.entries(this.filters)) {
               if (key == 'is_safe') {
                  if (value >= 0) {
                     apiPath += `&${key}=${value}`
                  }
               } else if (key == 'date_from') {
                  if (value && value !== "" && value !== "Invalid date") {
                     const utcDate = this.toUTC(value, false)
                     if (utcDate && utcDate !== "Invalid date") {
                        apiPath += `&${key}=${utcDate}`
                     }
                  }
               } else if (key == 'date_to') {
                  if (value && value !== "" && value !== "Invalid date") {
                     const utcDate = this.toUTC(value, true)
                     if (utcDate && utcDate !== "Invalid date") {
                        apiPath += `&${key}=${utcDate}`
                     }
                  } else if (this.filters['date_from'] && this.filters['date_from'] !== "" && this.filters['date_from'] !== "Invalid date") {
                     // Bound to end of date_from's day if date_to is not specified
                     const utcDate = this.toUTC(this.filters['date_from'], true)
                     if (utcDate && utcDate !== "Invalid date") {
                        apiPath += `&${key}=${utcDate}`
                     }
                  }
               } else {
                  if (value !== null && value !== "") {
                     apiPath += `&${key}=${value}`
                  }
               }
            }

            apiPath += `&for_export=0`
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.logs === "No Record Found" || !response.data.logs) {
                  if (this.pagination.page === 1) {
                     this.logs = []
                  }
                  this.logCount = 0
               }
               else {
                  if (this.pagination.page === 1) {
                     this.logs = response.data.logs
                  } else {
                     this.logs.push(...response.data.logs)
                  }
                  this.logCount = response.data.logs.length
               }
            })
            .catch((error) => {
               this.$toast.error(extractErrorMessage(error, "Failed to load log history."))
            })
      },
   },

})


</script>
<style scoped>
.card {
   border: none;
}

/* Top bar */
.top-bar {
   display: flex;
   align-items: center;
   justify-content: space-between;
   flex-wrap: wrap;
   gap: 12px;
   margin-bottom: 12px;
}

.export-group {
   display: flex;
   gap: 6px;
   flex-wrap: wrap;
}

.btn-export {
   background: #fff;
   border: 1px solid #dee2e6;
   color: #435666;
   font-size: 13px;
   font-weight: 500;
   padding: 6px 14px;
   border-radius: 8px;
   transition: all 0.15s ease;
}

.btn-export:hover:not(:disabled) {
   background: #00a0c1;
   border-color: #00a0c1;
   color: #fff;
}

.btn-export:disabled {
   opacity: 0.4;
   cursor: not-allowed;
}

.quick-filters {
   display: flex;
   gap: 12px;
   flex-wrap: wrap;
}

.filter-item {
   display: flex;
   flex-direction: column;
   gap: 2px;
}

.filter-item label {
   font-size: 11px;
   font-weight: 600;
   color: #6c757d;
   text-transform: uppercase;
   letter-spacing: 0.3px;
}

.filter-item .form-select {
   border-radius: 8px;
   font-size: 13px;
   min-width: 120px;
   border-color: #dee2e6;
}

/* Filter panel */
.filter-panel {
   background: #f8f9fa;
   border-radius: 10px;
   padding: 16px;
   margin-bottom: 12px;
}

.filter-label {
   font-size: 11px;
   font-weight: 600;
   color: #6c757d;
   text-transform: uppercase;
   letter-spacing: 0.3px;
   margin-bottom: 4px;
}

.filter-input {
   border-radius: 8px !important;
   font-size: 13px;
   border-color: #dee2e6;
}

.btn-clear-filters {
   background: #fff;
   border: 1px solid #dee2e6;
   color: #6c757d;
   font-size: 12px;
   border-radius: 8px;
   padding: 6px 10px;
}

.btn-clear-filters:hover {
   background: #f3994d;
   border-color: #f3994d;
   color: #fff;
}

/* Table */
.table-scroll-container {
   overflow: auto;
   max-height: 55vh;
   min-height: 300px;
   border: 1px solid #e9ecef;
   border-radius: 10px;
}

.log-table {
   font-size: 13px;
   margin-bottom: 0;
}

.log-table thead th {
   position: sticky;
   top: 0;
   z-index: 2;
   background: #f5f6f8;
   font-size: 12px;
   font-weight: 600;
   color: #6c757d;
   text-transform: uppercase;
   letter-spacing: 0.3px;
   border-bottom: 2px solid #e9ecef;
   padding: 10px 12px;
   white-space: nowrap;
}

.log-table tbody tr {
   transition: background-color 0.1s ease;
}

.log-table tbody tr:hover {
   background-color: #f8f9fa;
}

.log-table td {
   padding: 8px 12px;
   color: #435666;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
}

.detail-link {
   cursor: pointer;
   color: #00a0c1;
   font-size: 14px;
}

.detail-link:hover {
   color: #008299;
}

.date-cell {
   font-size: 12px;
   color: #6c757d;
}

/* Status badges */
.status-badge {
   font-size: 11px;
   font-weight: 600;
   padding: 3px 8px;
   border-radius: 12px;
   white-space: nowrap;
}

.status-safe { background: #e6f9f0; color: #00A54F; }
.status-unsafe { background: #ffe8e9; color: #e74c3c; }
.status-blocked { background: #ffe8e9; color: #e74c3c; }
.status-unchecked { background: #e6f6f8; color: #00a0c1; }

.type-badge {
   font-size: 11px;
   font-weight: 500;
   padding: 2px 6px;
   border-radius: 10px;
}

.type-phishing { background: #ffe8e9; color: #e74c3c; }
.type-categories { background: #fff3e6; color: #f3994d; }
.type-ai-categories { background: #f3e8ff; color: #9333ea; }
.type-government-tlds { background: #e0f2fe; color: #0369a1; }
.type-download { background: #ecfdf5; color: #047857; }
.type-upload { background: #e0e7ff; color: #4338ca; }
.type-regular { background: #f1f5f9; color: #475569; }
.type-block-all-websites { background: #fee2e2; color: #991b1b; }
.type-regular-ip { background: #f8fafc; color: #64748b; }
.type-http-block { background: #fef3c7; color: #b45309; }
.type-unsafe { background: #fef2f2; color: #dc2626; }
.type-default { background: #f0f1f3; color: #6c757d; }

/* Login field indicators */
.badge-yes {
   color: #00A54F;
   font-size: 12px;
}

.badge-no {
   color: #ccc;
   font-size: 10px;
}

/* Footer */
.table-footer {
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 12px 4px 0;
}

.total-badge {
   font-size: 13px;
   font-weight: 500;
   color: #435666;
}

.scroll-hint {
   font-size: 13px;
   font-weight: 600;
   color: #00a0c1;
   animation: pulse 2s infinite;
}

.end-hint {
   font-size: 13px;
   color: #adb5bd;
}

@keyframes pulse {
   0%, 100% { opacity: 1; }
   50% { opacity: 0.5; }
}

/* Print */
.print-table {
   display: none;
}

/* Modal */
.modal-backdrop-custom {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background-color: rgba(0, 0, 0, 0.5);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
}

.modal-content-custom {
   position: relative;
   background-color: white;
   padding: 2rem;
   border-radius: 14px;
   width: 100%;
   max-width: 500px;
   box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
   display: flex;
   flex-direction: column;
   max-height: 80vh;
}

.modal-title-custom {
   font-weight: 700;
   font-size: 1.1rem;
   color: #435666;
   margin-bottom: 0;
}

.modal-content-custom hr {
   margin-top: 0.75rem;
   margin-bottom: 1rem;
   border-color: #e9ecef;
}

.close-button-custom {
   position: absolute;
   top: 0.75rem;
   right: 1rem;
   font-size: 1.8rem;
   font-weight: bold;
   line-height: 1;
   color: #adb5bd;
   background: none;
   border: none;
   cursor: pointer;
}

.close-button-custom:hover {
   color: #435666;
}

.modal-body-custom {
   overflow-y: auto;
   font-size: 14px;
   color: #435666;
}

.modal-footer-custom {
   margin-top: 1rem;
   text-align: right;
}

/* Detail Modal Sections */
.detail-section {
   margin-bottom: 14px;
}

.detail-label {
   font-size: 11px;
   font-weight: 600;
   color: #6c757d;
   text-transform: uppercase;
   letter-spacing: 0.3px;
   margin-bottom: 4px;
}

.detail-value {
   font-size: 14px;
   color: #1e293b;
   font-weight: 500;
}

.detail-value--highlight {
   font-family: monospace;
   font-size: 14px;
   font-weight: 600;
   padding: 8px 12px;
   border-radius: 8px;
   word-break: break-all;
}

.detail-value--tld {
   background: #eef2ff;
   color: #4338ca;
   border: 1px solid #c7d2fe;
}

.detail-value--ai {
   background: #f5f3ff;
   color: #7c3aed;
   border: 1px solid #ddd6fe;
}

.detail-value--category {
   background: #fffbeb;
   color: #b45309;
   border: 1px solid #fde68a;
}

.detail-value--phishing {
   background: #fef2f2;
   color: #dc2626;
   border: 1px solid #fecaca;
}

.detail-badge {
   display: inline-block;
   font-size: 12px;
   font-weight: 600;
   padding: 4px 10px;
   border-radius: 6px;
}

.detail-badge--reason {
   background: #fee2e2;
   color: #dc2626;
}

.detail-description-text {
   font-size: 13px;
   line-height: 1.4;
   color: #4b5563;
   margin-top: 6px;
}

.detail-text {
   font-size: 13px;
   white-space: pre-wrap;
   word-break: break-word;
   color: #475569;
   margin-bottom: 0;
}
</style>